const CurrencyFilterModel = {};

CurrencyFilterModel.FIELDS = {
  SWITCHER_TABS: () => ({
    id: 1,
    name: 'Tab switcher',
    component: 'switcher-tabs',
    props: {
      tabs: undefined, // === required
      mode: 'currency',
    },
    emit: 'input',
    valueKey: 'value',
    value: null,
    prepareValue: (value) => value.value,
    prepareProps: (data) => {
      if (!data) {
        return {};
      }

      const props = {};

      if (data.product_currency_setting_field_values) {
        props.tabs = data.product_currency_setting_field_values
          .map((value) => ({ title: value.value }));
      }

      return props;
    },
  }),

  SELECT_REGULAR: () => ({
    id: 2,
    name: 'Select',
    component: 'ui-kit-select',
    props: {
      items: undefined, // === required,
      textAppendix: {},
      value: null,
      placeholder: null,
      label: null,
      inlineLabel: false,
      canHaveEmptyValue: true,
      valueField: 'title',
    },
    emit: 'input',
    valueKey: 'value',
    value: null,
    prepareValue: (value) => value.value,
    prepareProps: (data) => {
      if (!data) {
        return {};
      }
      const props = {};

      if (data.placeholder) {
        props.placeholder = data.placeholder;
        props.label = data.placeholder;
      }

      if (data.component_view === CurrencyFilterModel.ID_BY_FIELD.MULTIPLE_SEARCH_INPUT) {
        props.inlineLabel = true;
      }

      if (data.product_currency_setting_field_values) {
        props.items = data.product_currency_setting_field_values
          .map((value) => ({ id: value.id, title: value.value }));
      }

      return props;
    },
  }),

  SEARCH_INPUT: () => ({
    id: 3,
    name: 'Select with search',
    component: 'search-input',
    props: {
      searchResults: undefined, // === required
      'is-data-list-mode': true, // === default value
      placeholder: '', // === default value
      textAppendix: {},
    },
    emit: 'input',
    valueKey: 'value',
    value: null,
    prepareValue: (value) => value.value,
    prepareProps: (data) => {
      if (!data) {
        return {};
      }

      const props = {};

      if (data.product_currency_setting_field_values) {
        props.searchResults = data.product_currency_setting_field_values
          .map((value) => value.value);
      }

      if (data.placeholder) {
        props.placeholder = data.placeholder;
      }

      return props;
    },
  }),

  INPUT: () => ({
    id: 4,
    name: 'Regular input',
    component: 'ui-input',
    props: {
      type: 'number',
      min: 0,
      max: null,
      placeholder: '',
      textAppendix: {},
      disabled: false,
    },
    emit: 'input',
    valueKey: 'value',
    value: null,
    prepareValue: (value) => value,
    prepareProps: (data) => {
      if (!data) {
        return {};
      }

      const props = {};

      if (data.has_custom_value && data.component_edit_data) {
        props.min = data.component_edit_data?.validation_rules?.min;
        props.max = data.component_edit_data?.validation_rules?.max;
      }

      const fillableProps = [
        'placeholder',
        'textAppendix',
        'disabled',
        'type',
        'inputmode',
        'min',
        'max',
      ];

      fillableProps.forEach((prop) => {
        if (data[prop]) {
          props[prop] = data[prop];
        }
      });

      return props;
    },
  }),

  MULTIPLE_SEARCH_INPUT: () => ({
    id: 5,
    component: 'multiple-search-input',
    name: 'Multiple search input',
    props: {
      image: '',
      titleField: 'value',
      placeholder: 'Search items',
      searchResults: [],
      shouldClearEmptyValue: true,
      shouldEmitEmptySearch: true,
    },
    emit: 'input',
    valueKey: 'values',
    value: [],
    prepareValue: (defaultValue, fieldData) => {
      if (!Array.isArray(defaultValue)) {
        return [];
      }

      if (!fieldData.has_custom_value && fieldData.product_currency_setting_field_values) {
        return defaultValue.map((item) => ({
          id: item.id,
          value: item.value,
          image: item.additional_value,
          priority: item.priority,
        })).sort((a, b) => a.priority - b.priority);
      }

      if (fieldData.component_view_data) {
        return defaultValue?.map((item) => ({
          value: item.title,
          min: item.min,
          max: item.max,
        }));
      }

      return [];
    },
    prepareProps: (data) => {
      if (!data) {
        return {};
      }

      const props = {};

      if (!data.has_custom_value && data.product_currency_setting_field_values) {
        props.searchResults = data.product_currency_setting_field_values
          .map((item) => ({
            id: item.id,
            value: item.value,
            image: item.additional_value,
            priority: item.priority,
          }))
          .sort((a, b) => b.priority - a.priority);
      } else if (data.component_view_data) {
        props.searchResults = data.component_view_data.values
          ?.map((item) => ({
            value: item.title,
            min: item.min,
            max: item.max,
          }));
      }

      const fillableProps = [
        'placeholder',
        'disabled',
      ];

      fillableProps.forEach((prop) => {
        if (data[prop]) {
          props[prop] = data[prop];
        }
      });

      return props;
    },
  }),

  DROPDOWN_RANGE: () => ({
    id: 6,
    component: 'dropdown-range',
    name: 'Dropdown range',
    props: {
      placeholder: 'Select range',
      range: {},
    },
    emit: 'input',
    valueKey: 'values',
    value: [],
    prepareValue: () => [],
    prepareProps: (data) => {
      if (!data || !data.has_custom_value || !data.component_view_data?.values.length) {
        return {};
      }

      const values = data.default_value
        ? data.default_value
        : [data.component_view_data.values?.[0]?.min, data.component_view_data.values?.[0]?.max];

      return {
        range: {
          values,
          borderValues: [data.component_view_data.values?.[0]?.min, data.component_view_data.values?.[0]?.max],
          step: 1,
          zones: [
            {
              from: data.component_view_data.values?.[0]?.min,
              to: data.component_view_data.values?.[0]?.max,
              size: 100,
              step: 1,
            },
          ],
          scaleStepsCount: 1,
        },
        placeholder: data.placeholder,
      };
    },
  }),

  MULTIPLE_GRID_SELECT: () => ({
    id: 7,
    component: 'multiple-grid-select',
    name: 'Multiple grid select',
    props: {
      image: '',
      titleField: 'value',
      placeholder: 'Search items',
      searchResults: [],
      shouldClearEmptyValue: true,
      shouldEmitEmptySearch: true,
    },
    emit: 'input',
    valueKey: 'values',
    value: [],
    prepareValue: () => [],
    prepareProps: (data) => {
      if (!data) {
        return {};
      }

      const props = {};

      if (!data.has_custom_value && data.product_currency_setting_field_values) {
        props.searchResults = data.product_currency_setting_field_values
          .map((item) => ({
            value: item.value,
            image: item.additional_value,
            priority: item.priority,
          }))
          .sort((a, b) => a.priority - b.priority);
      }

      const fillableProps = [
        'placeholder',
        'disabled',
      ];

      fillableProps.forEach((prop) => {
        if (data[prop]) {
          props[prop] = data[prop];
        }
      });

      return props;
    },
  }),
};

CurrencyFilterModel.FIELD_BY_ID = {
  1: CurrencyFilterModel.FIELDS.SWITCHER_TABS,
  2: CurrencyFilterModel.FIELDS.SELECT_REGULAR,
  3: CurrencyFilterModel.FIELDS.SEARCH_INPUT,
  4: CurrencyFilterModel.FIELDS.INPUT,
  5: CurrencyFilterModel.FIELDS.MULTIPLE_SEARCH_INPUT,
  6: CurrencyFilterModel.FIELDS.DROPDOWN_RANGE,
  7: CurrencyFilterModel.FIELDS.MULTIPLE_GRID_SELECT,
};

CurrencyFilterModel.ID_BY_FIELD = {
  SWITCHER_TABS: 1,
  SELECT_REGULAR: 2,
  SEARCH_INPUT: 3,
  INPUT: 4,
  MULTIPLE_SEARCH_INPUT: 5,
  DROPDOWN_RANGE: 6,
  MULTIPLE_GRID_SELECT: 7,
};

CurrencyFilterModel.GOLD_FIELDS = [
  CurrencyFilterModel.FIELDS.SWITCHER_TABS(),
  CurrencyFilterModel.FIELDS.SELECT_REGULAR(),
  CurrencyFilterModel.FIELDS.SEARCH_INPUT(),
];

CurrencyFilterModel.EDIT_FIELDS = [
  CurrencyFilterModel.FIELDS.SELECT_REGULAR(),
  CurrencyFilterModel.FIELDS.MULTIPLE_GRID_SELECT(),
  CurrencyFilterModel.FIELDS.INPUT(),
];

CurrencyFilterModel.VIEW_FIELDS = [
  CurrencyFilterModel.FIELDS.MULTIPLE_SEARCH_INPUT(),
  CurrencyFilterModel.FIELDS.DROPDOWN_RANGE(),
];

CurrencyFilterModel.BAKED_FIELDS = () => {
  const result = [];

  for (let i = 0; i < Object.entries(CurrencyFilterModel.FIELDS).length; i++) {
    const entry = Object.entries(CurrencyFilterModel.FIELDS)[i];
    const field = entry[1]();
    if ('name' in field) {
      result.push(field);
    }
  }

  return result;
};

export default CurrencyFilterModel;
